import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function RegisterPage() {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    try {
      await register(form.email, form.password, form.full_name);
      toast.success("Welcome to Scentodore.");
      navigate("/account");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    }
  };

  return (
    <main data-testid="register-page" className="relative min-h-[70vh] sd-dark sd-dark-noise">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none opacity-70" />
      <div className="relative max-w-md mx-auto px-5 sm:px-6 py-14 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4B278]">
            <Sparkles className="h-3 w-3" /> Join the Society
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[#FAF8F5]">Create an account.</h1>
          <p className="mt-3 text-sm text-[#FAF8F5]/70">Earn 1 point per $1 spent. Track shipments. Save engravings.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs text-[#FAF8F5]/70 uppercase tracking-[0.14em]">Full Name</Label>
              <Input required data-testid="register-name"
                value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 text-[#FAF8F5] rounded-xl" />
            </div>
            <div>
              <Label className="text-xs text-[#FAF8F5]/70 uppercase tracking-[0.14em]">Email</Label>
              <Input type="email" required autoComplete="email" data-testid="register-email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 text-[#FAF8F5] rounded-xl" />
            </div>
            <div>
              <Label className="text-xs text-[#FAF8F5]/70 uppercase tracking-[0.14em]">Password</Label>
              <Input type="password" required autoComplete="new-password" data-testid="register-password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 text-[#FAF8F5] rounded-xl" />
              <div className="mt-1 text-[10px] text-[#FAF8F5]/50">Minimum 6 characters</div>
            </div>
            {error && <div data-testid="register-error" className="text-xs text-red-300">{error}</div>}
            <Button type="submit" data-testid="register-submit" disabled={loading}
              className="w-full bg-[#C5A059] text-[#0B0A08] hover:bg-[#D4B278] rounded-xl py-6 text-sm uppercase tracking-[0.16em] shadow-[0_10px_30px_rgba(197,160,89,0.35)]">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account</> : "Create Account"}
            </Button>
          </form>

          <div className="mt-8 text-center text-sm text-[#FAF8F5]/70">
            Already a member? <Link to="/login" data-testid="register-login-link" className="text-[#D4B278] hover:underline">Sign in</Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
